import { C as FARBIG, PALETTES, FONT, FONT_MEDIUM, FONT_BOLD, FONT_ITALIC, FONT_MEDIUM_ITALIC } from './tokens.js';
import { vectorSource, treeSource, GLYPHS, SPRITE, hoehenSource } from './tiles.js';


const SWISSTOPO_ATTRIB =
  '<a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">&copy; swisstopo</a>';
// SWISSIMAGE stammt teils aus Satellitenaufnahmen und verlangt laut swisstopo
// eine eigene Quellenangabe - nicht bloss "swisstopo".
const SWISSIMAGE_ATTRIB =
  '<a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">' +
  'CNES, Spot Image, swisstopo, NPOC</a>';
// Die Velonetze kommen vom Bundesamt fuer Strassen, nicht von swisstopo -
// das verlangt eine eigene Nennung.
const ASTRA_ATTRIB =
  '<a href="https://www.schweizmobil.ch" target="_blank" rel="noopener">ASTRA / SchweizMobil</a>';
const GEOADMIN_WMS = 'https://wms.geo.admin.ch/';
// Farben der Veloauflage. Nicht in tokens.js, weil sie in *jedem* Kartenbild
// gleich bleiben sollen - wie das Radar und die Touren.
const VELO = '#0A7E8C';
const VELO_RAU = '#9A6A2E';

const WMTS = (layer, fmt = 'png') =>
  `https://wmts.geo.admin.ch/1.0.0/${layer}/default/current/3857/{z}/{x}/{y}.${fmt}`;

/**
 * Groesse der Symbolplaketten. Sie waechst mit dem Zoom wie die Schrift auch -
 * eine feste Zahl wirkt weit draussen zu schwer und nah zu zierlich. Die
 * Plakette misst 24 px, der Faktor bringt sie auf das Mass der urspruenglichen
 * OSM-Zeichen (rund 20 px) und darueber hinaus.
 */
/**
 * Farbtreppe der Schummerung, Helligkeit -15 bis 0.
 *
 * swisstopo legt sie als 16 feste Stufen an. Nachgerechnet sind die Stufen
 * eine gerade Mischung zwischen dem dunkelsten Ton und dem Untergrund - Probe
 * bei -7/-8: deren rgb(227,232,234) und rgb(224,230,231) liegen genau auf der
 * Geraden. Deshalb hier gerechnet statt abgeschrieben: so traegt jede Palette
 * ihren eigenen Ton, und die Base Map (dunkelste Stufe #ADBCC7) unterscheidet
 * sich von der Leichten Basiskarte (#C8D2D5) nur durch eine Zahl.
 */
function reliefTreppe(dunkel, grund) {
  const z = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [dr, dg, db] = z(dunkel);
  const [gr, gg, gb] = z(grund);
  const paare = [];
  for (let i = 0; i <= 15; i++) {
    const t = (15 - i) / 15;                       // -15 -> 1, 0 -> 0
    const mix = (a, b) => Math.round(b + t * (a - b));
    paare.push(-15 + i,
      `#${[mix(dr, gr), mix(dg, gg), mix(db, gb)]
        .map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase()}`);
  }
  return ['match', ['get', 'luminosity'], ...paare, grund];
}

const ICON = ['interpolate', ['linear'], ['zoom'], 12, 0.6, 15, 0.8, 18, 1];

/** Zoomabhängige Linienbreite: [[z, w], ...] -> interpolate-Ausdruck */
const w = (stops) => ['interpolate', ['linear'], ['zoom'], ...stops.flat()];

/**
 * Schriftgrad waechst exponentiell mit, nicht linear - sonst wirkt die
 * Beschriftung beim Hineinzoomen schnell zu klein. Basis 1.25 wie in
 * swisstopos eigenen Vektorkacheln; die Stuetzwerte sind von dort abgelesen.
 */
const ts = (stops) => ['interpolate', ['exponential', 1.25], ['zoom'], ...stops.flat()];

/**
 * Deckkraft aller Gebaeudeebenen - eine Kurve, von niemandem abweichend.
 * Sie steigt durchgehend; ein Zwischenhoch wuerde beim Hineinzoomen als
 * zweite Stufe gelesen, auch wenn der Endwert stimmt.
 *
 * Kurz gehalten: ein Drittel einer Zoomstufe. Lang genug, dass nichts
 * aufspringt, kurz genug, dass man nicht minutenlang halbdurchsichtige
 * Haeuser ansieht.
 */
const GEBAEUDE_DECKUNG = ['interpolate', ['linear'], ['zoom'], 14, 0, 14.3, 1];

/** Gleise haben eine Breite, nicht mehrere - darum feste Werte statt Rampe. */
const RAIL_W = 1.6;
const RAIL_LIGHT_W = 1.1;

/** Beschriftung bevorzugt deutsch, sonst lokaler Name */
/**
 * Beschriftung: der amtliche Name vor dem deutschen Exonym.
 *
 * `name:de` wirkte zunaechst richtig, setzt aber die deutsche Uebersetzung
 * durch - gemessen in OSM: St. Gallen traegt `name=St. Gallen`, aber
 * `name:de=Sankt Gallen`, und Milano wird so zu Mailand. Eine Landeskarte
 * schreibt Orte so an, wie sie vor Ort heissen. `name` liefert genau das,
 * auch bei zweisprachigen Gemeinden wie Biel/Bienne.
 *
 * Unbedenklich, weil die Kacheln nur die Schweiz und ihre Nachbarn abdecken -
 * alles in lateinischer Schrift. Fuer eine Weltkarte braeuchte es `name:latin`.
 */
const NAME = ['coalesce', ['get', 'name'], ['get', 'name:de'], ['get', 'name_de']];

/**
 * Wichtige Punkte früh, Nebensächliches spät. Haltestellen sind in OSM sehr
 * dicht und kämen sonst schon bei Zoom 14 flächendeckend.
 */
/**
 * Farbgruppen der POI-Beschriftung, uebernommen aus swisstopos Vektorstil.
 * Dort ist der Normalfall fast schwarz; Farbe tragen nur diese Gruppen.
 * Alles Uebrige - Gastronomie, Laeden, Banken, Hotels - bleibt neutral.
 */
const POI_FARBGRUPPEN = [
  ['poiVerkehr', ['bus', 'railway', 'rail', 'tram', 'subway', 'aerialway', 'airport',
    'aerodrome', 'ferry_terminal', 'harbor', 'parking', 'bicycle_parking',
    'bicycle_rental', 'car_rental', 'car_sharing', 'charging_station', 'taxi', 'fuel']],
  ['poiHistorisch', ['place_of_worship', 'monastery', 'attraction', 'monument', 'castle',
    'ruins', 'memorial', 'artwork', 'museum', 'art_gallery', 'theatre']],
  ['poiNatur', ['park', 'cemetery', 'viewpoint', 'picnic_site', 'playground', 'pitch',
    'garden', 'dog_park', 'recycling', 'pharmacy', 'camp_site', 'golf']],
  ['poiAmtlich', ['embassy', 'courthouse', 'prison', 'townhall', 'police', 'library',
    'fire_station', 'post']],
  ['poiWasser', ['hospital', 'clinic', 'swimming_pool', 'marina', 'waterfall', 'water_park']],
  ['poiHuette', ['alpine_hut', 'wilderness_hut', 'shelter']],
  ['poiBildung', ['school', 'college', 'university', 'kindergarten']]
];

/** Aus den Gruppen einen `match`-Ausdruck bauen. */
const poiFarbe = (C) => [
  'match', ['get', 'class'],
  ...POI_FARBGRUPPEN.flatMap(([token, klassen]) => [klassen, C[token] || C.poi]),
  C.poi
];

export const POI_SPLIT = {
  // Nicht jeder Punkt traegt einen Rang. Ohne `coalesce` scheitert der Vergleich
  // an null, und der Punkt faellt aus beiden Ebenen: er fehlt dann auf der Karte.
  'poi-symbol': ['all', ['<=', ['coalesce', ['get', 'rank'], 99], 8], ['!=', ['get', 'class'], 'bus']],
  'poi-minor': ['any', ['>', ['coalesce', ['get', 'rank'], 99], 8], ['==', ['get', 'class'], 'bus']]
};

const fill = (id, sourceLayer, filter, color, extra = {}) => ({
  id,
  type: 'fill',
  source: 'osm',
  'source-layer': sourceLayer,
  ...(filter ? { filter } : {}),
  paint: { 'fill-color': color, 'fill-antialias': true, ...extra }
});

const line = (id, sourceLayer, filter, paint, layout = {}) => ({
  id,
  type: 'line',
  source: 'osm',
  'source-layer': sourceLayer,
  ...(filter ? { filter } : {}),
  layout: { 'line-cap': 'round', 'line-join': 'round', ...layout },
  paint
});

/**
 * Strassenklassen. In kleinen Massstäben bleiben die Linien schlank und
 * wachsen erst ab Zoom 14 auf die Breite, die im Kartenbild gewünscht ist.
 */
/**
 * Verlauf der Bodenbedeckung ueber den Zoom.
 *
 * In der Uebersicht gehoert dem Gelaende das Bild - swisstopo setzt ihre
 * Bodenbedeckung unter Zoom 10 auf null und faehrt sie erst dann hoch
 * (gemessen: 0 bis Zoom 9, dann 0.1 / 0.125 / 0.135 / 0.15 bei 10 / 11 / 12 /
 * 14, also 67 %, 83 %, 90 %, 100 % des Nahwerts). Dieselben Anteile stehen
 * hier, bezogen auf den Zielwert der jeweiligen Palette. Ohne das lag die
 * Flaeche auch weit draussen schon voll auf - in "Classic" sogar deckend,
 * womit das Relief dort vollstaendig verschwand.
 */
/**
 * swisstopos Staffelung der Ortsnamen.
 *
 * Sie bemessen die Schrift nicht pauschal, sondern nach `rank` - je kleiner
 * die Zahl, desto wichtiger der Ort - und bei den kleinen Namen zusaetzlich
 * nach Art. Die Werte stammen eins zu eins aus `omt_place_city`,
 * `omt_place_town_village` und `omt_place_other` ihrer Base Map.
 */
const rang = (...paare) => {
  const faelle = [];
  for (let i = 0; i < paare.length - 1; i += 2) {
    faelle.push(['>=', paare[i], ['coalesce', ['get', 'rank'], 99]], paare[i + 1]);
  }
  return ['case', ...faelle, paare[paare.length - 1]];
};

/** Kleine Ortsnamen: Quartier, Ortsteil, Insel, Dorf - jedes mit eigener Groesse. */
const kleinGross = (suburb, nachbar, insel, dorf) => ['match', ['get', 'class'],
  'suburb', suburb,
  ['neighbourhood', 'quarter'], nachbar,
  'island', insel,
  'village', dorf,
  nachbar];

const lcVerlauf = (ziel) => ['interpolate', ['linear'], ['zoom'],
  5, 0, 9, 0, 10, ziel * 0.67, 11, ziel * 0.83, 12, ziel * 0.9, 14, ziel];

/** Gletscher bleiben auch in der Uebersicht stehen - sie sind Orientierung. */
const eisVerlauf = (ziel) => ['interpolate', ['linear'], ['zoom'],
  4, 0, 6, ziel, 14, ziel];

const roadClasses = (C) => ({
  motorway: {
    case: C.motorwayCase, fill: C.motorway, minzoom: 5,
    width: [[5, 0.6], [8, 0.8], [10, 3.5], [12, 4.5], [14, 4.5], [15, 7], [16, 11], [18, 11], [20, 22]]
  },
  trunk: {
    case: C.trunkCase, fill: C.trunk, minzoom: 6,
    width: [[6, 0.5], [9, 3], [10, 3.5], [12, 4.5], [14, 4.5], [15, 7], [16, 11], [18, 11], [20, 22]]
  },
  primary: {
    case: C.primaryCase, fill: C.primary, minzoom: 7,
    width: [[7, 0.5], [9, 2.5], [10, 3.5], [12, 4], [14, 4.5], [15, 6.5], [16, 10], [18, 10], [20, 20]]
  },
  secondary: {
    case: C.secondaryCase, fill: C.secondary, minzoom: 9,
    width: [[9, 2.5], [10, 3], [12, 3.5], [14, 4], [15, 5.5], [16, 9], [18, 9], [20, 18]]
  },
  tertiary: {
    case: C.tertiaryCase, fill: C.tertiary, minzoom: 10,
    width: [[10, 2.5], [12, 3], [14, 3.5], [15, 4.5], [16, 8], [18, 8], [20, 16]]
  },
  minor: {
    case: C.minorCase, fill: C.minor, minzoom: 12,
    width: [[12, 2.5], [14, 3], [15, 3.5], [16, 7], [18, 7], [20, 14]]
  },
  service: {
    // Vor die Gebaeude, nicht mit ihnen: Zufahrten, Fusswege und Haeuser
    // setzten alle bei Zoom 14 ein, also im selben Augenblick. swisstopo
    // laesst saemtliche gewoehnlichen Strassen bei Zoom 13 beginnen.
    case: C.serviceCase, fill: C.service, minzoom: 13,
    width: [[13, 2.5], [15, 3], [16, 6], [18, 6], [20, 12]]
  }
});

/**
 * Der Rand ist ein Streifen fester Breite, kein Anteil der Linie.
 *
 * Vorher wuchs er mit (Faktor 1.4 plus ein halbes Pixel) - bei Zoom 16 waren
 * das 4,25 px, bei Zoom 20 fast 10. Gemessen an swisstopos eigenem Stil ist
 * ihr Randstreifen nie breiter als 1,5 px: (Randbreite minus Fuellbreite)
 * geteilt durch zwei ergibt dort 0,25 bei Zoom 10, 0,5 bei Zoom 12 bis 14 und
 * 1,0 bis 1,5 ab Zoom 16. Genau diese Treppe steht hier.
 */
const RAND = [[8, 0.25], [11, 0.375], [12, 0.5], [14, 0.5], [15, 0.75], [16, 1.0], [18, 1.0], [20, 2.0]];
const RAND_GROSS = [[8, 0.375], [11, 0.5], [12, 0.5], [13, 0.75], [14, 0.75], [15, 1.0], [16, 1.5], [18, 1.5], [20, 3.0]];

/** Randbreite bei einem Zoom - linear zwischen den Stufen. */
function randBei(zoom, treppe) {
  if (zoom <= treppe[0][0]) return treppe[0][1];
  for (let i = 1; i < treppe.length; i++) {
    const [z0, v0] = treppe[i - 1];
    const [z1, v1] = treppe[i];
    if (zoom <= z1) return v0 + ((zoom - z0) / (z1 - z0)) * (v1 - v0);
  }
  return treppe[treppe.length - 1][1];
}

const casingWidth = (stops, gross) =>
  stops.map(([z, v]) => [z, v + 2 * randBei(z, gross ? RAND_GROSS : RAND)]);

function roadLayers(C) {
  const ROAD_CLASSES = roadClasses(C);
  const layers = [];
  const order = ['service', 'minor', 'tertiary', 'secondary', 'primary', 'trunk', 'motorway'];

  // Erst alle Casings, dann alle Füllungen -> saubere Kreuzungen
  for (const key of order) {
    const r = ROAD_CLASSES[key];
    layers.push(
      line(
        `road-${key}-case`,
        'transportation',
        ['all', ['==', ['get', 'class'], key], ['!=', ['get', 'brunnel'], 'tunnel']],
        {
          'line-color': r.case,
          'line-width': w(casingWidth(r.width, key === 'motorway' || key === 'trunk'))
        },
        // Rund wie die Fuellung: eine Sackgasse endete sonst mit zwei stumpf
        // abbrechenden Randlinien und einer offenen Luecke dazwischen. An
        // Einmuendungen stoert es nicht, weil alle Raender vor allen
        // Fuellungen liegen - die groessere Strasse deckt den Ueberstand zu.
        { 'line-cap': 'round' }
      )
    );
    layers[layers.length - 1].minzoom = r.minzoom;
  }
  for (const key of order) {
    const r = ROAD_CLASSES[key];
    layers.push(
      line(
        `road-${key}`,
        'transportation',
        ['all', ['==', ['get', 'class'], key], ['!=', ['get', 'brunnel'], 'tunnel']],
        { 'line-color': r.fill, 'line-width': w(r.width) }
      )
    );
    layers[layers.length - 1].minzoom = r.minzoom;
  }
  return layers;
}

/**
 * Ein- und Ausblenden statt Umschalten.
 *
 * MapLibre zeichnet eine Ebene unterhalb ihres `minzoom` gar nicht - sie
 * erscheint also schlagartig. Hier bekommt jede Ebene mit Zoomgrenze eine
 * Deckkraft-Rampe ueber eine halbe Zoomstufe, damit nichts aufspringt.
 *
 * Ebenen, die schon eine eigene Rampe mitbringen, bleiben unberuehrt: dort war
 * der Verlauf Absicht und meist sorgfaeltiger gewaehlt als eine Faustregel.
 */
const EINBLENDEN = 0.35;

const DECKKRAFT = {
  fill: ['fill-opacity'],
  line: ['line-opacity'],
  symbol: ['text-opacity', 'icon-opacity'],
  raster: ['raster-opacity'],
  'fill-extrusion': ['fill-extrusion-opacity'],
  circle: ['circle-opacity', 'circle-stroke-opacity']
};

function weicheUebergaenge(layers) {
  for (const l of layers) {
    const schluessel = DECKKRAFT[l.type];
    if (!schluessel) continue;
    const min = Number.isFinite(l.minzoom) ? l.minzoom : null;
    const max = Number.isFinite(l.maxzoom) ? l.maxzoom : null;
    if (min === null && max === null) continue;

    // Bei engen Fenstern die Spanne kuerzen, sonst ueberschneiden sich die
    // Stuetzpunkte und der Ausdruck ist ungueltig.
    const spanne = min !== null && max !== null
      ? Math.min(EINBLENDEN, (max - min) / 2.5)
      : EINBLENDEN;
    if (spanne <= 0.01) continue;

    l.paint = { ...(l.paint || {}) };
    for (const k of schluessel) {
      const jetzt = l.paint[k];
      if (Array.isArray(jetzt)) continue;
      const voll = typeof jetzt === 'number' ? jetzt : 1;
      const stops = [];
      if (min !== null) stops.push(min, 0, min + spanne, voll);
      if (max !== null) stops.push(max - spanne, voll, max, 0);
      if (stops.length < 4) continue;
      l.paint[k] = ['interpolate', ['linear'], ['zoom'], ...stops];
    }
  }
  return layers;
}

export function buildStyle({ palette = 'farbig' } = {}) {
  const C = PALETTES[palette] || FARBIG;
  // Breite wie bei swisstopo zoomabhaengig: in der Uebersicht fast nichts,
  // im grossen Massstab knapp ueber einem Pixel. Das Luftbild hebt sie an.
  const hf = C.haloFactor ?? 1;
  const haloW = (max) => w([[12, 0.5 * hf], [14, 0.9 * hf], [16, max * hf]]);
  /**
   * Ortsnamen wachsen bis 30 px - ein Schleier von 1,25 px traegt die dann
   * nicht mehr. Er waechst deshalb mit: auf hellem Grund bleibt er unsichtbar,
   * auf schwarzen Gebaeudebloecken macht er die Schrift ueberhaupt erst lesbar.
   */
  const haloPlace = w([[12, 0.6 * hf], [14, 1.3 * hf], [16, 2.1 * hf], [18, 2.8 * hf]]);
  return {
    version: 8,
    name: 'KGVA Landeskarte',
    glyphs: GLYPHS,
    sprite: SPRITE,
    sources: {
      osm: vectorSource(),
      // Relief als *Vektor*: Schummerung, Felszeichnung und Geroell. swisstopo
      // fuehrt das neben der Basiskarte als eigene Quelle - Ausdehnung
      // 3.57..13.66 Ost, 44.18..48.88 Nord, also samt Nachbarlaendern.
      // Dadurch braucht es weder eine eigene Rechnerei noch ein Rasterbild.
      // Rasterschummerung als Unterlage. swisstopos Vektorrelief fuehrt ab
      // Zoom 10 fast nur noch beleuchtete Haenge und endet jenseits der Grenze
      // ganz - in Innsbruck bei Zoom 13 null Flaechen, waehrend Chamonix und
      // Aosta noch traegt. swissALTIRegio deckt dagegen den ganzen Alpenraum
      // ab. swisstopo haelt es in beiden eigenen Stilen genauso: Raster unten,
      // Vektor darueber.
      // Weltweite Schummerung, ohne Schluessel und ohne Kontingent.
      //
      // swisstopo loest dieselbe Aufgabe mit MapTilers kostenpflichtigem
      // Dienst (`api.maptiler.com/tiles/hillshade`, ihr Schluessel steht offen
      // im Stil) - deshalb antwortet ihre Karte ausserhalb der Schweiz mit
      // HTTP 403, wenn man sie ohne Schluessel laedt.
      //
      // Esris World Hillshade ist frei abrufbar und liegt qualitativ gleichauf
      // mit der Schweizer Zeichnung: am selben Ort gemessen eine
      // Helligkeitsstreuung von 33.0 gegen 32.6 auf der Schweizer Seite. Eine
      // selbst gerechnete Schummerung aus dem weltweiten 30-Meter-Modell kam
      // dort nur auf 17.4 - die Daten dahinter sind feiner, wo nationale
      // Laserbefliegungen vorliegen.
      reliefWelt: {
        type: 'raster',
        tiles: ['https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}'],
        // Die Kacheln sind 256 px gross, hier aber als 128 angemeldet: damit
        // holt MapLibre fuer dieselbe Flaeche die naechsthoehere Zoomstufe und
        // zeichnet sie halb so gross. Esri liefert JPEG, und dessen
        // 8-Pixel-Bloecke springen gemessen 19 % staerker als der uebrige
        // Bildinhalt - auf dem Schirm sind sie so nur noch vier Punkte breit.
        tileSize: 128,
        minzoom: 0,
        // Zoom 16 ist die hoechste Stufe, die *ueberall* Daten hat. Stufe 17
        // gibt es nur im Alpenraum - gemessen traegt sie im Karwendel 21 KB,
        // in Muenchen, Genua, Mailand und Lyon dagegen eine graue Kachel mit
        // der Aufschrift "Map data not yet available" (2521 Byte).
        //
        // Weil die Kacheln als 128 px angemeldet sind, fragt MapLibre ohnehin
        // eine Stufe hoeher als angezeigt - mit 17 hier landete man bei
        // Bildzoom 16 also genau auf dieser Platzhalterkachel. Mit 16 zieht
        // MapLibre nah die letzte echte Stufe auf, was zugleich die weichere
        // Zeichnung gibt, die im Nahbereich ohnehin besser passt.
        maxzoom: 16,
        attribution:
          '<a href="https://www.arcgis.com/home/item.html?id=1b243539f4514b6ba35e7d995890db1d" target="_blank" rel="noopener">Esri World Hillshade</a>'
      },
      reliefRegio: {
        type: 'raster',
        tiles: [WMTS('ch.swisstopo.swissaltiregio-reliefschattierung_multidirektional')],
        tileSize: 256,
        minzoom: 2,
        // Gemessen bis Zoom 16 ueberall vorhanden - darueber zieht MapLibre
        // die letzte Stufe auf, was fuer eine Schummerung genuegt.
        maxzoom: 16,
        attribution: SWISSTOPO_ATTRIB
      },
      reliefVektor: {
        type: 'vector',
        url: 'https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.relief.vt/v1.0.0/tiles.json',
        attribution: SWISSTOPO_ATTRIB
      },

      aerial: {
        type: 'raster',
        tiles: [WMTS('ch.swisstopo.swissimage', 'jpeg')],
        tileSize: 256,
        minzoom: 2,
        maxzoom: 19,
        attribution: SWISSIMAGE_ATTRIB
      },
      // Offizielle Vektorkacheln von swisstopo: Hoehenkurven, Hoehenkoten und
      // Flurnamen aus der Landeskarte - genauer als alles aus einem globalen
      // Hoehenmodell gerechnete, aber nur fuer die Schweiz.
      swisstopo: {
        type: 'vector',
        url: 'https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.base.vt/v1.0.0/tiles.json',
        attribution: SWISSTOPO_ATTRIB
      },
      // Hoehenmodell fuer die Gelaendedarstellung. Kostet nichts, solange
      // `setTerrain` nicht gesetzt ist - dann wird keine Kachel geladen.
      gelaende: {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        // Fuer das 3D-Gitter genuegte Zoom 13 - dort glaettet MapLibre beim
        // Ueberzoomen besser, als die Stufen es zulassen. Die Schummerung
        // dagegen lebt von der Aufloesung: jede Stufe mehr schaerft die
        // Haenge, weil sie aus den Hoehenunterschieden *benachbarter* Punkte
        // gerechnet wird.
        maxzoom: 15,
        attribution:
          '<a href="https://registry.opendata.aws/terrain-tiles/" target="_blank" rel="noopener">Terrain Tiles</a>'
      },
      // Velonetze des Bundes (ASTRA, SchweizMobil). Das sind die *signalisierten*
      // Routen - wer der Beschilderung folgen will, braucht genau diese Linien
      // und nicht die Velowege aus OSM. In unseren eigenen Kacheln stehen sie
      // nicht: das OpenMapTiles-Schema fuehrt nur Wander- und Strassenrouten
      // als Relation, keine Velorouten (nachgezaehlt in vier Stadtkacheln:
      // lwn, nwn, rwn und Strassennetze - kein lcn, rcn oder ncn).
      veloland: {
        type: 'raster',
        tiles: [WMTS('ch.astra.veloland')],
        tileSize: 256,
        minzoom: 7,
        maxzoom: 17,
        attribution: ASTRA_ATTRIB
      },
      mtbland: {
        type: 'raster',
        tiles: [WMTS('ch.astra.mountainbikeland')],
        tileSize: 256,
        minzoom: 7,
        maxzoom: 17,
        attribution: ASTRA_ATTRIB
      },
      // Sperrungen und Umleitungen gibt es nur als WMS - kein Kachelsatz.
      // MapLibre setzt {bbox-epsg-3857} selbst ein und fragt damit 256er
      // Ausschnitte ab, was einem Kachelsatz gleichkommt.
      veloSperren: {
        type: 'raster',
        tiles: [`${GEOADMIN_WMS}?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0`
          + '&LAYERS=ch.astra.veloland-sperrungen_umleitungen'
          + '&FORMAT=image/png&TRANSPARENT=true&CRS=EPSG:3857'
          + '&WIDTH=256&HEIGHT=256&BBOX={bbox-epsg-3857}'],
        tileSize: 256,
        minzoom: 9,
        attribution: ASTRA_ATTRIB
      },
      ...(treeSource() ? { trees: treeSource() } : {}),
      ...(hoehenSource() ? { hoehenAusland: hoehenSource() } : {})
    },
    layers: weicheUebergaenge([
      { id: 'background', type: 'background', paint: { 'background-color': C.paper } },

      // Umschaltbare Hintergründe (nur Schweiz), standardmässig aus
      {
        id: 'bg-aerial',
        type: 'raster',
        source: 'aerial',
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 1 }
      },

      // ---------- Relief ----------
      // Unterste Lage: eine selbst gerechnete Schummerung aus dem weltweiten
      // Hoehenmodell, das ohnehin fuer die 3D-Ansicht bereitliegt. MapLibre
      // rechnet sie auf der Grafikkarte, es braucht also keine eigenen
      // Kacheln.
      //
      // Noetig, weil swisstopos Quellen beide an einer Grenze enden: das
      // Vektorrelief liefert ab Zoom 12 jenseits der Landesgrenze leere
      // Kacheln (20 Byte in Innsbruck und Garmisch), und swissALTIRegio deckt
      // zwar den Alpenbogen, aber nicht das ganze Kartenfeld - in Genua und
      // Richtung Wien kommt ein leeres Bild, an den Kartenecken HTTP 400,
      // und Stuttgart hat bei Zoom 10 noch Daten, bei Zoom 14 nicht mehr.
      //
      // Keine Maske noetig - swisstopo macht es in ihrer eigenen Base Map
      // genauso: dort liegt ein weltweites Raster auf Platz 1 ohne jeden
      // Filter, und ihre Vektorschummerung auf Platz 2 deckt es ab Zoom 8
      // einfach zu (`fill-opacity` 0.5 -> 1). Wo sie keine Flaechen hat - an
      // den besonnten Haengen, die ihr Filter `luminosity <= 0` auslaesst -
      // scheint das Globale durch und fuellt die Luecke. Genau so hier.
      {
        id: 'relief-welt',
        type: 'raster',
        source: 'reliefWelt',
        layout: { visibility: 'none' },
        paint: {
          // Ausgewaschen aufgelegt, wie swisstopo ihr weltweites Raster haelt
          // (`raster-brightness-min` 0.8, `raster-contrast` 0.3). Sonst
          // erschlaegt die Schummerung das Kartenbild.
          // Kein Kontrast: der haette die JPEG-Bloecke mitverstaerkt. Die
          // Staerke kommt stattdessen aus der Deckkraft - gerechnet aus der
          // Kachel selbst (10%-Quantil 154, 90% 244): bei 0.55 ueber Papier
          // ergibt das Schatten um 199 und Licht um 248.
          'raster-opacity': ['interpolate', ['linear'], ['zoom'], 2, 0.45, 6, 0.8, 13, 0.8, 16, 0.55],
          'raster-contrast': 0,
          'raster-saturation': palette === 'sw' ? -1 : 0,
          'raster-resampling': 'linear'
        }
      },
      // Darüber die Schummerung von swisstopo selbst - swissALTIRegio, fertig
      // gerechnet und feiner als alles, was sich aus einem 30-Meter-Modell
      // ableiten laesst. Nur in der Uebersicht: bei Zoom 16 traten die
      // Bildpunkte der Quelle als feine Rechtecke hervor.
      {
        id: 'relief-raster',
        type: 'raster',
        source: 'reliefRegio',
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': ['interpolate', ['linear'], ['zoom'], 11, 1, 13.5, 0],
          'raster-brightness-min': ['interpolate', ['linear'], ['zoom'], 2, 0.6, 4, 0.5, 10, 0.8],
          'raster-contrast': ['interpolate', ['linear'], ['zoom'], 2, 0, 4, 0.2, 10, 0.3],
          'raster-saturation': palette === 'sw' ? -1 : 0,
          'raster-resampling': 'linear'
        }
      },
      // Die Vektorschummerung daruebergelegt - deckend, mit swisstopos
      // Farbtreppe. `fill-antialias: false` ist wichtig: sonst zeichnet
      // MapLibre an jeder Flaechenkante eine feine Linie, und aus der
      // Schummerung wird ein Netz.
      {
        id: 'relief-global',
        type: 'fill',
        source: 'reliefVektor',
        'source-layer': 'hillshade',
        filter: ['<=', ['get', 'luminosity'], 0],
        layout: { visibility: 'none' },
        paint: {
          'fill-antialias': false,
          'fill-color': reliefTreppe(C.reliefDunkel, C.reliefHell),
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 1, 0.5, 8, 1]
        }
      },

      // ---------- Bodenbedeckung ----------
      // Unter Zoom 14 liefern die Kacheln kaum Gebäude – dann trägt die
      // Siedlungsfläche das Bild, wie im kleinen Massstab der Landeskarte auch.
      fill('lc-settlement', 'landuse', ['match', ['get', 'class'], ['residential', 'suburb', 'quarter', 'neighbourhood'], true, false], C.settlement, {
        // Die Siedlungsflaeche vertritt die Gebaeude, solange es keine gibt.
        // Sie nimmt sich ueber genau dieselbe Spanne zurueck, in der die
        // Gebaeude aufblenden (14 bis 15.2) - was die eine verliert, gewinnt
        // die andere.
        // swisstopo blendet die Siedlungsflaeche ab Zoom 12 ganz aus - dort
        // tragen schon die Gebaeude. Unsere Kacheln fuehren Gebaeude erst ab
        // Zoom 14, deshalb bleibt sie laenger stehen, verschwindet dann aber
        // ebenso vollstaendig statt als Restschleier liegenzubleiben.
        'fill-opacity': ['interpolate', ['linear'], ['zoom'],
          9, C.siedlungDeckung * 0.6, 11, C.siedlungDeckung, 13.5, C.siedlungDeckung,
          14, C.siedlungDeckung * 0.5, 15, 0]
      }),
      // Kulturland bleibt ungefaerbt: swisstopos Base Map fasst in ihrer
      // Bodenbedeckung nur Wald, Gebuesch, Park, Gletscher und Sand - Acker
      // und Wiese traegt dort der Untergrund. Ein Gruenschleier darueber hatte
      // bei uns 71 % der Bildpunkte eingefaerbt statt der 18 %, die swisstopo
      // am selben Ort zeigt. Die Ebene bleibt bestehen, damit der Schalter
      // etwas bewirkt, faellt aber kaum auf.
      fill('lc-farmland', 'landcover', ['==', ['get', 'class'], 'farmland'], C.farmland, { 'fill-opacity': lcVerlauf(C.farmlandDeckung) }),
      // Wiese und Ried wie Kulturland: swisstopos Bodenbedeckung kennt die
      // Unterklassen gar nicht, dort bleibt die Flaeche offen. Nur ein Hauch,
      // damit die Ebene ueberhaupt etwas zeigt.
      fill('lc-grass', 'landcover', ['match', ['get', 'class'], ['grass', 'wetland'], true, false], C.grass, { 'fill-opacity': lcVerlauf(C.farmlandDeckung) }),
      fill('lc-sand', 'landcover', ['==', ['get', 'class'], 'sand'], C.sand, { 'fill-opacity': lcVerlauf(C.lcDeckung) }),
      fill('lc-rock', 'landcover', ['==', ['get', 'class'], 'rock'], C.rock, { 'fill-opacity': lcVerlauf(C.lcDeckung) }),
      fill('lc-glacier', 'landcover', ['==', ['get', 'class'], 'ice'], C.glacier, { 'fill-opacity': eisVerlauf(C.lcDeckung) }),
      fill('lc-scrub', 'landcover', ['match', ['get', 'subclass'], ['scrub', 'heath'], true, false], C.scrub, { 'fill-opacity': lcVerlauf(C.forestDeckung) }),
      fill('lc-forest', 'landcover', ['==', ['get', 'class'], 'wood'], C.forest, { 'fill-opacity': lcVerlauf(C.forestDeckung) }),
      line('lc-forest-edge', 'landcover', ['==', ['get', 'class'], 'wood'], {
        'line-color': C.forestEdge,
        'line-width': w([[10, 0.4], [14, 1]]),
        'line-opacity': 0.8
      }),
      fill('lc-park', 'park', null, C.grass, { 'fill-opacity': lcVerlauf(C.parkDeckung) }),

      // ---------- Einzelbäume ----------
      // Die Landeskarte hebt einzelne Bäume hervor; im OpenMapTiles-Schema
      // fehlen sie, darum ein eigenes kleines Archiv (deploy/trees.yml).
      {
        id: 'tree-row',
        type: 'line',
        source: 'trees',
        'source-layer': 'tree_row',
        minzoom: 15,
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': C.tree,
          'line-width': w([[15, 1.4], [18, 3]]),
          'line-dasharray': [0.1, 1.6]
        }
      },
      {
        id: 'tree-single',
        type: 'circle',
        source: 'trees',
        'source-layer': 'tree',
        minzoom: 15,
        paint: {
          'circle-color': ['match', ['get', 'leaf_type'], 'needleleaved', C.treeConifer, C.tree],
          'circle-radius': w([[15, 1], [17, 2.1], [19, 3.4]]),
          'circle-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0.55, 16.5, 0.9]
        }
      },

      // ---------- Höhenlinien ----------
      // Zwischenkurven alle 10 m, Hauptkurven alle 100 m - so zeichnet es die
      // Landeskarte. Die Werte kommen direkt aus swisstopos Kurvensatz.
      {
        id: 'contour-minor',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        // Hier lag der Fehler: alle Zwischenkurven ab Zoom 12. swisstopo
        // staffelt nicht ueber `minzoom`, sondern ueber die Linienbreite -
        // wer dort bei Zoom 11 bis 13 hinschaut, sieht *nur* Hunderterkurven
        // (alles andere bekommt Breite 0). Die Zwanziger kommen bei 14 dazu,
        // die Zehner erst bei 18. Zwei Stufen zu frueh war also genau richtig
        // beobachtet.
        minzoom: 14,
        filter: ['all', ['!', ['in', ['get', 'class'], ['literal', ['rock', 'ice', 'water']]]], ['!=', ['%', ['get', 'ele'], 100], 0]],
        layout: { 'line-join': 'round' },
        paint: {
          // Auf Geroell zeichnet swisstopo die Kurven in durchsichtigem
          // Schwarz statt in Braun - und das Gewicht steckt in der Farbe,
          // nicht in einer zusaetzlichen Deckkraft.
          'line-color': ['match', ['get', 'class'], 'scree', C.contourScree, C.contour],
          // Zehnerkurven bleiben bis Zoom 18 unsichtbar - dort unterscheidet
          // swisstopo 0.75 / 1 / 1.5 fuer die Zwanziger und 1.25 fuer den Rest.
          // Ab Zoom 18 kommen die Zehnerkurven dazu - gemessen am Eiger sind
          // das 564 von 1129 Kurven, also die Mehrheit. Sie stehen ein wenig
          // zurueck hinter den Zwanzigern (1.0 gegen 1.1), aber deutlich
          // ueber swisstopos Verhaeltnis von 1.25 zu 1.5: dort wirkten sie zu
          // zaghaft.
          'line-width': ['interpolate', ['exponential', 1.25], ['zoom'],
            14, ['case', ['==', ['%', ['get', 'ele'], 20], 0], 0.55, 0],
            15, ['case', ['==', ['%', ['get', 'ele'], 20], 0], 0.75, 0],
            18, ['case', ['==', ['%', ['get', 'ele'], 20], 0], 1.1, 1.0]
          ]
        }
      },
      {
        id: 'contour-main',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        // swisstopo setzt die Hunderterkurven bei Zoom 11 an, nicht bei 10.
        minzoom: 11,
        filter: ['all', ['!', ['in', ['get', 'class'], ['literal', ['rock', 'ice', 'water']]]], ['==', ['%', ['get', 'ele'], 100], 0]],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': ['match', ['get', 'class'], 'scree', C.contourScree, C.contourMain],
          // Breiten aus deren Stil: 0.75 bei Zoom 11, 1 bei 12, 1.5 bei 14,
          // 2 bei 15, 3 bei 18.
          // swisstopos Zahlen waren 0.75/1/1.5/2/3. Gemessen sind unsere
          // Striche gleich breit wie ihre (Median 2 px in beiden Bildern),
          // auf unserem Kartenbild wirken sie aber schwerer - vermutlich,
          // weil unsere Felsschraffur zarter liegt und weniger verdeckt.
          // Deshalb ein Viertel zurueck.
          'line-width': w([[11, 0.6], [12, 0.75], [14, 1.1], [15, 1.4], [18, 2.2]])
        }
      },

      {
        // ---------- Höhenlinien ausserhalb der Schweiz ----------
        // Eigenes Archiv aus Copernicus GLO-30, bei dem Schweiz und
        // Liechtenstein beim Zuschnitt bereits herausgeschnitten sind - so
        // haelt es swisstopo mit ihrer Quelle `contours_swisstopo_borders`
        // auch. Deshalb braucht es hier keinen Filter und keine Maske.
        id: 'contour-welt-main',
        type: 'line',
        source: 'hoehenAusland',
        'source-layer': 'hoehenlinie',
        minzoom: 11,
        filter: ['in', ['get', 'stufe'], ['literal', ['s1', 's2', 's3']]],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': C.contourMain,
          // swisstopos Zahlen waren 0.75/1/1.5/2/3. Gemessen sind unsere
          // Striche gleich breit wie ihre (Median 2 px in beiden Bildern),
          // auf unserem Kartenbild wirken sie aber schwerer - vermutlich,
          // weil unsere Felsschraffur zarter liegt und weniger verdeckt.
          // Deshalb ein Viertel zurueck.
          'line-width': w([[11, 0.6], [12, 0.75], [14, 1.1], [15, 1.4], [18, 2.2]])
        }
      },
      {
        id: 'contour-welt-minor',
        type: 'line',
        source: 'hoehenAusland',
        'source-layer': 'hoehenlinie',
        minzoom: 14,
        filter: ['==', ['get', 'stufe'], 's4'],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': C.contour,
          // Alle Zwischenkurven sind hier die Zwanziger - sie bekommen
          // dieselbe Haarbreite wie in der Schweiz.
          'line-width': w([[14, 0.55], [15, 0.75], [18, 1.1]])
        }
      },
      {
        id: 'contour-eis',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 13,
        filter: ['in', ['get', 'class'], ['literal', ['ice', 'water']]],
        layout: { 'line-join': 'round' },
        paint: {
          // Auf Gletscher und unter Wasser zeichnet swisstopo die Kurven blau
          // (rgb(77,164,218)), in einer eigenen Ebene ab Zoom 13 - und mit
          // *derselben* Staffelung wie die braunen: nur die Hunderter tragen
          // Gewicht, die Zwanziger sind Haarlinien, alles dazwischen bleibt
          // bis Zoom 18 unsichtbar. Eine durchgehende Breite liess auf dem
          // Gletscher jede Kurve gleich schwer wirken.
          'line-color': C.waterEdge,
          'line-width': ['interpolate', ['exponential', 1], ['zoom'],
            13, ['case', ['==', ['%', ['get', 'ele'], 100], 0], 0.75, 0],
            14, ['case', ['==', ['%', ['get', 'ele'], 100], 0], 1.1,
                         ['==', ['%', ['get', 'ele'], 20], 0], 0.55, 0],
            15, ['case', ['==', ['%', ['get', 'ele'], 100], 0], 1.4,
                         ['==', ['%', ['get', 'ele'], 20], 0], 0.75, 0],
            18, ['case', ['==', ['%', ['get', 'ele'], 100], 0], 2.2,
                         ['==', ['%', ['get', 'ele'], 20], 0], 1.1, 0.9]
          ],
          // Deren Deckkraft fuer die blauen: 0.3 bei Zoom 13, 0.47 ab 14.
          'line-opacity': ['step', ['zoom'], 0, 13, 0.3, 14, 0.47]
        }
      },
      {
        // Höhenangaben fürs Ausland - wie die schweizerischen, nur aus dem
        // eigenen Archiv. Beschriftet werden die Hunderterkurven, also die
        // Stufen s1 bis s3.
        id: 'contour-welt-label',
        type: 'symbol',
        source: 'hoehenAusland',
        'source-layer': 'hoehenlinie',
        minzoom: 12.5,
        filter: ['in', ['get', 'stufe'], ['literal', ['s1', 's2', 's3']]],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 10], [14, 10.5], [16, 12], [18, 13]]),
          'text-max-angle': 25,
          'text-padding': 8,
          'text-rotation-alignment': 'map'
        },
        paint: { 'text-color': C.contourLabel, 'text-halo-color': C.haloContour, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'contour-label',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 12.5,
        filter: ['all', ['!', ['in', ['get', 'class'], ['literal', ['rock', 'ice', 'water']]]], ['==', ['%', ['get', 'ele'], 100], 0]],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 10], [14, 10.5], [16, 12], [18, 13]]),
          'text-max-angle': 25,
          'text-padding': 8,
          'text-rotation-alignment': 'map'
        },
        paint: { 'text-color': C.contourLabel, 'text-halo-color': C.haloContour, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },

      // ---------- Wasser ----------
      // See und Meer heller als Fluss, Weiher und Hafenbecken - in der Base
      // Map ein gemessener Unterschied von rgb(210,238,255) zu rgb(181,225,253).
      fill('water-fill', 'water', ['!=', ['get', 'brunnel'], 'tunnel'],
        ['match', ['get', 'class'], ['ocean', 'lake'], C.water, C.waterRiver]),
      // Die Felszeichnung dagegen gehoert nach oben: bei swisstopo steht sie
      // hinter der Wasserflaeche, damit der Stiftstrich auf dem Gelaende liegt
      // und nicht darunter verschwindet.
      // Felszeichnung - der Stiftstrich, der die Landeskarte ausmacht.
      // Klasse 1 ist Fels, alles Uebrige Eis.
      {
        id: 'relief-hachure',
        type: 'fill',
        source: 'reliefVektor',
        'source-layer': 'hachure',
        filter: ['==', ['geometry-type'], 'Polygon'],
        layout: { visibility: 'none' },
        paint: {
          'fill-antialias': false,
          'fill-color': ['match', ['get', 'class'], 1, '#0C0C0C', '#1985C8'],
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0, 11, 0.27]
        }
      },

      line('water-edge', 'water', ['!=', ['get', 'brunnel'], 'tunnel'], {
        'line-color': C.waterEdge,
        'line-width': w([[6, 0.3], [10, 0.7], [14, 1.3]]),
        'line-opacity': 0.9
      }),
      line('water-river', 'waterway', ['match', ['get', 'class'], ['river', 'canal'], true, false], {
        'line-color': C.river,
        'line-width': w([[8, 0.6], [12, 1.8], [16, 4.5], [20, 12]])
      }),
      line('water-stream', 'waterway', ['match', ['get', 'class'], ['stream', 'ditch', 'drain'], true, false], {
        'line-color': C.river,
        'line-width': w([[12, 0.5], [16, 1.6], [20, 5]]),
        'line-opacity': 0.85
      }),

      // Schiffsrouten
      line('water-ferry', 'transportation', ['==', ['get', 'class'], 'ferry'], {
        'line-color': C.waterEdge,
        'line-width': w([[9, 0.6], [14, 1.2], [18, 2]]),
        'line-dasharray': [4, 3],
        'line-opacity': 0.8
      }),

      // ---------- Tunnel (gedämpft, unter den Strassen) ----------
      line('road-tunnel', 'transportation', ['all', ['==', ['get', 'brunnel'], 'tunnel'], ['match', ['get', 'class'], ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor'], true, false]], {
        'line-color': C.minorCase,
        'line-width': w([[12, 1.5], [16, 6], [20, 16]]),
        'line-dasharray': [2, 2],
        'line-opacity': 0.55
      }),

      // ---------- Wege ----------
      // Wege zuerst: bei swisstopo ab Zoom 10 mit 0,75 px, lange bevor das
      // Quartierstrassennetz einsetzt.
      line('road-track', 'transportation', ['==', ['get', 'class'], 'track'], {
        'line-color': C.track,
        'line-width': w([[11, 0.4], [13, 0.6], [16, 1.3], [20, 3]]),
        'line-dasharray': [5, 2.5]
      }),
      // Trottoirs, Treppen, Velowege: braun gestrichelt
      line('road-path', 'transportation', ['all', ['==', ['get', 'class'], 'path'], ['!=', ['get', 'subclass'], 'path']], {
        'line-color': C.path,
        'line-width': w([[14, 0.7], [16, 1.4], [20, 4]]),
        'line-dasharray': [2, 2]
      }),
      // Wanderwege: auf der Landeskarte rot gestrichelt
      line('path-hiking', 'transportation', ['all', ['==', ['get', 'class'], 'path'], ['==', ['get', 'subclass'], 'path']], {
        'line-color': C.hiking,
        'line-width': w([[12, 0.8], [15, 1.3], [18, 2.2], [20, 3.4]]),
        'line-dasharray': [3, 2]
      }),

      // ---------- Flugplätze ----------
      fill('aeroway-area', 'aeroway', ['match', ['get', 'class'], ['runway', 'taxiway', 'apron'], true, false], C.aeroway),

      ...roadLayers(C),

      // ---------- Velo ----------
      // Eigene Farbe, bewusst ausserhalb der Landeskartenpalette: das ist eine
      // thematische Auflage wie das Radar oder die Touren, kein Kartenblatt.
      // Blaugruen, weil Rot schon den Wanderwegen und den Touren gehoert und
      // Blau dem Wasser.
      //
      // Standardmaessig aus - alle fuenf Ebenen haengen an der Gruppe "Velo".
      line('velo-weg', 'transportation', ['any',
        ['==', ['get', 'subclass'], 'cycleway'],
        ['==', ['get', 'bicycle'], 'designated']
      ], {
        'line-color': VELO,
        'line-width': w([[12, 0.8], [14, 1.6], [16, 2.6], [20, 7]]),
        'line-opacity': 0.9
      }, { visibility: 'none' }),
      // Wo man absteigen muss, ist kein Veloweg - das soll man sehen, bevor
      // man dort hinplant.
      line('velo-schieben', 'transportation', ['==', ['get', 'bicycle'], 'dismount'], {
        'line-color': VELO,
        'line-width': w([[14, 1.2], [16, 2], [20, 5]]),
        'line-dasharray': [1, 2],
        'line-opacity': 0.8
      }, { visibility: 'none' }),
      // Unbefestigt: fuer ein Rennvelo der Unterschied zwischen Weg und Umweg.
      // Das Schema kennt nur "paved" und "unpaved", feiner geht es nicht.
      line('velo-unbefestigt', 'transportation',
        ['all',
          ['==', ['get', 'surface'], 'unpaved'],
          ['match', ['get', 'class'], ['path', 'track', 'service', 'minor'], true, false]
        ], {
          'line-color': VELO_RAU,
          'line-width': w([[13, 1], [16, 2.2], [20, 6]]),
          'line-dasharray': [2, 1.5],
          'line-opacity': 0.75
        }, { visibility: 'none' }),

      // ---------- Bahn ----------
      // Feste Strichstaerke, bewusst ohne Zoomrampe: eine Liniensignatur hat
      // auf dem gedruckten Blatt eine Breite, nicht mehrere. Strecke und
      // Bahnhofsareal werden gleich dick gezeichnet - sie unterscheiden sich
      // nur darin, ab wann sie erscheinen.
      line('rail-base', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['!=', ['get', 'brunnel'], 'tunnel'], ['!', ['has', 'service']]], {
        'line-color': C.rail,
        'line-width': RAIL_W
      }),
      // Rangier-, Abstell- und Verbindungsgleise tragen in den Kacheln ein
      // `service`-Merkmal - bei Luzern die Haelfte aller Gleise. Alle zugleich
      // gezeichnet legen sie schon bei Zoom 13 einen roten Faecher ueber den
      // Bahnhof, deshalb kommen sie erst im groesseren Massstab dazu.
      {
        ...line('rail-service', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['!=', ['get', 'brunnel'], 'tunnel'], ['has', 'service']], {
          'line-color': C.rail,
          'line-width': RAIL_W
        }),
        minzoom: 14
      },
      // Bahntunnel: rot gestrichelt wie im Kartenbild, gleiche Breite
      line('rail-tunnel', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['==', ['get', 'brunnel'], 'tunnel']], {
        'line-color': C.rail,
        'line-width': RAIL_W,
        'line-dasharray': [3, 2.5],
        'line-opacity': 0.85
      }),
      // Tram, Metro, Standseilbahn: eigene, schlankere Signatur - ebenfalls fest
      line('rail-light', 'transportation', ['match', ['get', 'class'], ['transit'], true, false], {
        'line-color': C.railLight,
        'line-width': RAIL_LIGHT_W,
        'line-dasharray': [4, 2]
      }),

      // ---------- Gebäude ----------
      // Zoom 13 ist die unterste Stufe mit vollstaendigem Bestand. Die Kachel
      // meldet dort zwar nur *ein* Objekt - aber Planetiler verschmilzt auf
      // dieser Stufe alle benachbarten Gebaeude zu einem Mehrfachpolygon mit
      // tausenden Ringen, das Bild ist also komplett. Bei Zoom 12 ist die
      // Ebene wirklich leer, tiefer geht es nicht.
      //
      // Alle gemeinsam ein- und ausblenden, nicht stufenweise: sonst wirken
      // Einfamilienhausquartiere leer, obwohl sie dicht bebaut sind.
      // Eine Ebene, eine Kurve.
      //
      // Unterhalb Zoom 14 fuehrt das Archiv die Gebaeude nur als wenige
      // verschmolzene Bloecke (gemessen: 4 statt 167). Die mitzuzeichnen und
      // bei 14 gegen die feine Fassung zu tauschen hiesse, eine Formaenderung
      // zu verstecken - und die faellt auf, egal wie gut die Deckkraft passt.
      // Zwei gleichzeitige Kachelstufen kann MapLibre je Quelle nicht.
      //
      // Deshalb: gar keine groben Bloecke. Bis 14 traegt die Siedlungsflaeche,
      // ab 14 blenden die einzelnen Gebaeude auf.
      {
        ...fill('building-fill', 'building', null, C.building, {
          'fill-opacity': GEBAEUDE_DECKUNG
        }),
        minzoom: 14
      },

      // Nur im hellen Bild. Bei fast weisser Fuellung ist die Kante nicht
      // Zierde, sondern das Einzige, was die Haeuser sichtbar macht - deshalb
      // ab derselben Stufe wie die Fuellung und etwas kraeftiger.
      ...(C.buildingEdge
        ? [{
            ...line('building-edge', 'building', null, {
              'line-color': C.buildingEdge,
              'line-width': w([[13, 0.5], [15, 0.7], [17, 1]]),
              // Dieselbe Rampe wie die Fuellung, sonst steht die Kante allein da.
              'line-opacity': GEBAEUDE_DECKUNG
            }),
            // Dieselbe Grenze wie die Fuellung: unterhalb liegen im Archiv nur
            // die verschmolzenen Bloecke, und die zeichnen wir nicht mehr.
            minzoom: 14
          }]
        : []),

      // Baukoerper. Standardmaessig aus - die Landeskarte ist ein flaches,
      // gezeichnetes Bild; 3D ist ein Zusatz, keine Grundeinstellung.
      // `hide_3d` markiert Teilflaechen, die sonst doppelt aufragen wuerden.
      {
        id: 'building-3d',
        type: 'fill-extrusion',
        source: 'osm',
        'source-layer': 'building',
        minzoom: 15,
        filter: ['!=', ['get', 'hide_3d'], true],
        layout: { visibility: 'none' },
        paint: {
          'fill-extrusion-color': C.building3d || C.building,
          'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 5],
          'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
          // Beim Hineinzoomen aufblenden, sonst springen die Haeuser auf.
          'fill-extrusion-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.8, 0.92]
        }
      },

      // ---------- Grenzen ----------
      line('boundary-canton-band', 'boundary', ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderBand,
        'line-width': w([[8, 1.5], [11, 3], [14, 5], [18, 9]]),
        'line-opacity': 0.4,
        'line-blur': 1
      }),
      line('boundary-canton', 'boundary', ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderCanton,
        'line-width': w([[5, 0.4], [11, 0.8], [18, 2.4]]),
        'line-opacity': 0.6
      }),
      // Erst der Saum, dann der Strich darueber - so liest sich die Grenze auch
      // dort noch, wo sie durch dichte Bebauung laeuft.
      line('boundary-country-band', 'boundary', ['all', ['<=', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderBand,
        'line-width': w([[4, 2.5], [8, 6], [12, 11], [16, 20]]),
        'line-opacity': 0.55,
        'line-blur': 1
      }),
      line('boundary-country', 'boundary', ['all', ['<=', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderCountry,
        'line-width': w([[3, 0.6], [8, 1.1], [14, 1.8]]),
        'line-opacity': 0.9
      }),

      // ---------- Velonetze des Bundes ----------
      // Ueber Strassen und Wegen, unter der Beschriftung: die Route soll man
      // auf der Strasse sehen, aber die Ortsnamen nicht verdecken.
      // Erst ab Zoom 11. Die Linienbreite steckt im Bild und waechst nicht mit
      // dem Massstab mit: in der Uebersicht liegt das Netz als blaue Decke
      // ueber der halben Schweiz und verdeckt alles darunter. Ab 11 ist es
      // eine Linie auf einer Karte.
      {
        id: 'velo-netz',
        type: 'raster',
        source: 'veloland',
        minzoom: 11,
        layout: { visibility: 'none' },
        // 0,7 statt deckend: darunter muss die Strasse lesbar bleiben, auf der
        // die Route verlaeuft. geo.admin.ch selbst zeichnet sie mit 0,6.
        paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 0 }
      },
      {
        id: 'velo-mtb',
        type: 'raster',
        source: 'mtbland',
        minzoom: 11,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 0 }
      },
      {
        id: 'velo-sperren',
        type: 'raster',
        source: 'veloSperren',
        minzoom: 11,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 0.9, 'raster-fade-duration': 0 }
      },

      // ---------- Beschriftung ----------
      {
        id: 'label-water',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'water_name',
        minzoom: 7,
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[8, 11], [14, 15], [18, 20]]),
          'text-max-width': 7,
          'symbol-placement': 'point'
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-waterway',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'waterway',
        minzoom: 12,
        filter: ['match', ['get', 'class'], ['river', 'canal'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[12, 10], [14, 11.5], [18, 15]]),
          'symbol-placement': 'line',
          'text-letter-spacing': 0.1
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-stream',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'waterway',
        minzoom: 14,
        filter: ['==', ['get', 'class'], 'stream'],
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 9.5], [16, 11.5], [18, 13]]),
          'symbol-placement': 'line',
          'text-letter-spacing': 0.08,
          'symbol-spacing': 400
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      // Hausnummern. Die Kacheln fuehren sie ab Zoom 14 (175 allein in der
      // Kachel Luzern), swisstopo zeigt sie ab 17 - frueher wird es Brei.
      {
        id: 'label-housenumber',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'housenumber',
        minzoom: 17,
        layout: {
          'text-field': ['get', 'housenumber'],
          'text-font': FONT,
          'text-size': ts([[17, 11], [19, 13]]),
          'text-padding': 2,
          'text-allow-overlap': false
        },
        paint: {
          'text-color': C.houseNumber,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1),
          'text-halo-blur': 0.25
        }
      },
      // Autobahnnummern. Die Kacheln fuehren diese Abschnitte meist ohne Namen,
      // nur mit `ref` ("A2") - unsere Beschriftung fragte bisher allein nach
      // dem Namen, deshalb blieben die Autobahnen unbeschriftet.
      {
        id: 'label-motorway',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'transportation_name',
        minzoom: 9,
        filter: ['all',
          ['match', ['get', 'class'], ['motorway', 'trunk'], true, false],
          ['has', 'ref']
        ],
        layout: {
          // Mehrfachnummern stehen als "24;25" - die erste genuegt.
          'text-field': ['slice', ['get', 'ref'], 0,
            ['case', ['>', ['index-of', ';', ['get', 'ref']], -1],
              ['index-of', ';', ['get', 'ref']], ['length', ['get', 'ref']]]],
          'text-font': FONT_MEDIUM,
          'text-size': ts([[9, 10], [13, 12], [16, 14]]),
          'symbol-placement': 'line',
          'text-rotation-alignment': 'map',
          'text-padding': 30,
          'symbol-spacing': 320
        },
        paint: {
          'text-color': C.motorwayCase,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1),
          'text-halo-blur': 0.25
        }
      },
      {
        id: 'label-road',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'transportation_name',
        minzoom: 13,
        filter: ['match', ['get', 'class'], ['ferry', 'path', 'track'], false, true],
        layout: {
          'text-field': NAME,
          'text-font': FONT,
          'text-size': ts([[13, 10], [16, 12], [18, 14]]),
          'symbol-placement': 'line',
          'text-rotation-alignment': 'map',
          'text-padding': 4
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-peak',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'mountain_peak',
        minzoom: 10,
        layout: {
          'text-field': ['format', NAME, {}, '\n', {}, ['concat', ['to-string', ['get', 'ele']], ' m'], { 'font-scale': 0.8 }],
          'text-font': FONT,
          'text-size': ts([[10, 10], [13, 12], [18, 15]]),
          'text-anchor': 'left',
          'text-offset': [0.7, 0],
          'icon-image': 'triangle_11',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.55, 16, 0.85],
          'text-optional': true
        },
        paint: {
          'text-color': C.peak,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-color': C.peak,
          'icon-halo-color': C.halo,
          'icon-halo-width': haloW(1)
        }
      },
      {
        id: 'poi-minor',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'poi',
        minzoom: 16,
        filter: ['all', ['==', ['geometry-type'], 'Point'], POI_SPLIT['poi-minor']],
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'class']], ['image', 'dot_11']],
          'icon-size': ICON,
          'text-field': ['step', ['zoom'], '', 17, NAME],
          'text-font': FONT,
          'text-size': ts([[13, 10.5], [18, 14]]),
          'text-anchor': 'top',
          'text-offset': [0, 0.75],
          'text-max-width': 8,
          'text-optional': true
        },
        paint: {
          // Bahnhoefe im Bahnrot (bzw. Dunkelblau im hellen Modus), alles
          // Uebrige in der Farbe seiner Gruppe - Normalfall fast schwarz.
          'text-color': ['match', ['get', 'class'],
            ['railway', 'rail'], C.stationLabel, poiFarbe(C)],
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-opacity': 0.9
        }
      },
      {
        id: 'poi-symbol',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'poi',
        minzoom: 14,
        filter: ['all', ['==', ['geometry-type'], 'Point'], POI_SPLIT['poi-symbol']],
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'class']], ['image', 'dot_11']],
          'icon-size': ICON,
          'text-field': ['step', ['zoom'], '', 15, NAME],
          'text-font': FONT,
          'text-size': ts([[7, 10], [13, 12], [18, 17]]),
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-max-width': 8,
          'text-optional': true,
          'icon-allow-overlap': false
        },
        paint: {
          // Bahnhoefe im Bahnrot (bzw. Dunkelblau im hellen Modus), alles
          // Uebrige in der Farbe seiner Gruppe - Normalfall fast schwarz.
          'text-color': ['match', ['get', 'class'],
            ['railway', 'rail'], C.stationLabel, poiFarbe(C)],
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-opacity': 0.95
        }
      },
      {
        id: 'spot-elevation',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'spot_elevation',
        minzoom: 13,
        layout: {
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 11], [14, 11.5], [16, 14], [18, 15]]),
          'text-anchor': 'left',
          'text-offset': [0.5, 0],
          'icon-image': 'dot_9',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 13, 0.45, 18, 0.7],
          'text-optional': true
        },
        paint: {
          'text-color': C.peak,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-color': C.peak,
          'icon-halo-color': C.halo,
          'icon-halo-width': haloW(1)
        }
      },
      {
        id: 'label-area-name',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'area_name',
        minzoom: 13,
        layout: {
          'text-field': ['coalesce', ['get', 'name'], ['get', 'name:de']],
          'text-font': FONT_MEDIUM_ITALIC,
          'text-size': ts([[13, 13], [18, 19]]),
          'text-max-width': 7,
          'text-letter-spacing': 0.04
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-small',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 10,
        maxzoom: 18,
        // 'locality' und 'farm' greifen heute noch nicht: das OpenMapTiles-Schema
        // kennt diese Klassen nicht, deshalb landen Flurnamen gar nicht erst in
        // den Kacheln. Mit einem eigenen Planetiler-Schema wuerden sie erscheinen.
        filter: ['match', ['get', 'class'],
          ['village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'isolated_dwelling',
           'locality', 'farm', 'island'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT,
          'text-size': ts([
            [7, kleinGross(15, 14, 13, rang(11, 14, 12, 13, 12))],
            [13, kleinGross(19, 17, 15, rang(11, 20, 12, 19, 18))],
            [18, kleinGross(25, 21, 19, rang(11, 27, 12, 25, 23))]
          ]),
          'text-max-width': 8
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-town',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 6,
        maxzoom: 17,
        filter: ['match', ['get', 'class'], ['town'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_MEDIUM,
          'text-size': ts([
            [7, rang(11, 16, 15)],
            [13, rang(11, 27, 23)],
            [18, rang(11, 41, 31)]
          ]),
          'text-max-width': 8,
          // In der Uebersicht gemischt (Kriens, Horw), im groesseren Massstab
          // tritt der Hauptort in Versalien hervor.
          'text-transform': ['step', ['zoom'], 'none', 12, 'uppercase'],
          'text-letter-spacing': ['step', ['zoom'], 0, 12, 0.09]
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-city',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 2,
        // Wie bei swisstopo: ab Zoom 14 tragen die Quartiernamen, der
        // Stadtname waere dort nur noch ein Riesenschriftzug ueber allem.
        // `weicheUebergaenge` blendet ihn aus, statt ihn abzuschneiden.
        maxzoom: 14,
        filter: ['match', ['get', 'class'], ['city'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_BOLD,
          'text-size': ts([
            [1, 11],
            [7, rang(3, 23, 4, 19, 18)],
            [13, rang(3, 39, 4, 35, 31)],
            [18, rang(3, 71, 4, 61, 51)]
          ]),
          'text-max-width': 9,
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.12
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-country',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        maxzoom: 9,
        filter: ['==', ['get', 'class'], 'country'],
        layout: {
          'text-field': NAME,
          'text-font': FONT_BOLD,
          'text-size': ts([[3, 11], [7, 16]]),
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.18,
          'text-max-width': 7
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      }
    ])
  };
}
